/*
 * Tools for creating TypeGuards.
 */
const isNull = u => u === null;

const isString = u => typeof u === 'string';

const isNumber = u => typeof u === 'number';

const isBoolean = u => typeof u === 'boolean';

const isOptional = guard => u => u === undefined || guard(u);

const isArrayOf = guard => u => Array.isArray(u) && u.every(guard);
/** TypeGuard for discriminated unions of strings */


const isOneOf = list => u => !!list.find(i => i === u);
/** TypeGuard for fixed values */


const isFixed = value => u => u === value; // type guard operators

/** TypeGuard for discriminated unions of objects
 *
 * Does not currently infer the array types, so `T` needs to be provided
 */


const isEither = list => u => !!list.find(g => g(u));

const isNullable = guard => u => {
  if (u === null) {
    return true;
  }

  const res = guard(u);
  return typeof res === 'boolean' && res;
};
/** Matches `lib/generatedGqlTypes`'s `Maybe<T>` - optional, nullable field */


const isMaybe = guard => isOptional(isNullable(guard));
/** *
 * TypeGuard for an object. Needs to have one Guard for every attribute
 *  */


const typeGuard = guards => u => {
  return Object.entries(guards).every(([key, guard]) => {
    if (!u) {
      return false;
    }

    const value = u[key];

    if (!guard(value)) {
      return false;
    }

    return true;
  });
};
/** *
 * TypeAssertion for an object. Needs to have one Guard or TypeAssertion for every attribute.
 * Will throw an error if the property is of the wrong type.
 *  */
// TODO: refactor the typeGuard function to work as a typeAssertion when called by a typeAssertion


function typeAssertion(guards) {
  return function assertGuard(u) {
    // for function Guards like `isEither`
    if (typeof guards === 'function') {
      const res = guards(u);

      if (typeof res === 'boolean' && !res) {
        throw new Error('failed-typeguard');
      }

      return;
    } // default with a Guard object


    Object.entries(guards).forEach(([key, guard]) => {
      if (!u) {
        throw new Error(`Can't read property '${key}' of '${u}'`);
      }

      const value = u[key];

      try {
        const res = guard(value);

        if (typeof res === 'boolean' && !res) {
          throw new Error('failed-typeguard');
        }
      } catch (e) {
        // TODO: write or install a nested error object
        throw new Error(`Value '${value}' has the wrong type for property '${key}':${'\n'}${e && e.message}`);
      }
    });
  };
}

const questionTypeFamilies = ['PRESENTATION', 'MULTIPLE_CHOICE', 'OPEN_ENDED', 'RATING_SCALE', 'MATRIX', 'RANKING', 'SLIDER', 'DATETIME', 'CONTACT_INFO', 'CLICK_MAP', 'FILE_UPLOAD'];
const questionTypeRequiredPredicateTypes = ['ALL', 'AT_LEAST', 'AT_MOST', 'EXACTLY', 'RANGE' // 'REQUIRED_ANSWERS_LIST',
];
const questionTypeVariants = ['TEXT', 'IMAGE', 'SINGLE_LINE', 'ESSAY', 'MANY_FIELDS', 'SINGLE_ANSWER_RADIO', 'SINGLE_ANSWER_SYMBOL', 'SINGLE_ANSWER_MENU', 'MANY_ANSWERS_CHECKBOX', 'MENU', 'SINGLE_SLIDER', 'DATE', 'TIME', 'DATETIME', 'MANY_FIELDS', 'SINGLE_CLICK', null];
const questionTypeLayoutPlacements = ['NEW_ROW', 'RIGHT_ALIGNED'];
const questionTypeValidationTypes = ['SUM', 'TEXT_LENGTH', 'EMAIL', 'INTEGER', 'DECIMAL', 'DATE_US', 'DATE_INTL', 'FILE_FORMAT'];
const questionTypeIds = ['single_textbox', 'comment_box'];
const questionTypeSortingKinds = ['FLIP', 'RANDOM', 'TEXT_ASC', 'TEXT_DESC'];
const questionTypeSkipLogicTypes = ['QUESTION_CONDITIONAL_COL', 'QUESTION_CONDITIONAL_ROW'];
const questionTypeSkipLogicDestinationTypes = ['DISQUALIFY_RESPONDENT', 'END_SURVEY', 'SKIP_TO_PAGE'];
const questionTypeMultipleChoiceLayouts = ['VERTICAL', 'THREE_COLUMNS', 'TWO_COLUMNS', 'HORIZONTAL'];
const SENTIMENTS = ['positive', 'negative', 'neutral', 'unknown'];
/** Possible icons for Star-Rating */

const ratingScaleSymbols = ['THUMB', 'STAR', 'HEART', 'SMILEY'];
/** Possible values for relative width (in percentage) */

const relativeWidthOptions = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
/** Possible values for fixed width (in pixel) */

const fixedWidthOptions = [100, 200, 300, 400, 500, 600, 700, 800]; // Base Types
// Question Guard Helpers

const isQuestionError = typeGuard({
  questionId: isNullable(isString),
  detail: isString,
  type: isOneOf([...questionTypeValidationTypes, 'REQUIRED'])
});

const baseQuestionGuards = (typeId, family, variant) => ({
  typeId: isFixed(typeId),
  variant: isFixed(variant),
  family: isFixed(family),
  id: isString,
  errors: isOptional(isArrayOf(isQuestionError))
});
/**
 * a very basic typeguard for the common fields for a Question Type.
 * should receive parameters for the actual ID, FAMILY and SUBTYPE of the
 * Question Type to avoid Typescript errors. Question types can use this function
 * to help build their own typeGuards
 */


const isQuestionType = (typeId, family, variant) => typeGuard(baseQuestionGuards(typeId, family, variant));

const isTextLengthValidation = typeGuard({
  errorMessage: isString,
  type: isFixed('TEXT_LENGTH'),
  minLength: isNumber,
  maxLength: isNumber
});
const isNumericalValidation = typeGuard({
  errorMessage: isString,
  type: isEither([isFixed('INTEGER'), isFixed('DECIMAL')]),
  minValue: isNumber,
  maxValue: isNumber
});
const isDateValidation = typeGuard({
  errorMessage: isString,
  type: isOneOf(['DATE_US', 'DATE_INTL']),
  minDate: isString,
  maxDate: isString
});
const isEmailValidation = typeGuard({
  errorMessage: isString,
  type: isFixed('EMAIL')
});
const isSumValidation = typeGuard({
  errorMessage: isString,
  type: isFixed('SUM'),
  sum: isNumber
});
const isFieldValidation = isEither([isTextLengthValidation, isNumericalValidation, isDateValidation, isEmailValidation]);
const isQuestionHeading = typeGuard({
  text: isString,
  image: isMaybe(typeGuard({
    id: isString,
    url: isString,
    alt: isMaybe(isString),
    s3Key: isMaybe(isString)
  }))
});
const headingGuards = typeGuard({
  default: isQuestionHeading,
  assignments: isMaybe(isArrayOf(typeGuard({
    id: isString,
    heading: isQuestionHeading,
    variable: typeGuard({
      id: isString,
      name: isNullable(isString),
      percent: isNumber,
      position: isNumber
    })
  })))
});
const positionGuard = {
  position: typeGuard({
    pageId: isString,
    inPage: isNumber,
    inSurvey: isNumber,
    label: isString
  })
};
const baseGuards$a = {
  id: isString
};
const isQuestionAnswer = typeGuard({
  id: isString,
  label: isString,
  visible: isBoolean
});
const externalQuizResultsGuards = {
  id: isString,
  feedback: typeGuard({
    correct: isString,
    partial: isString,
    incorrect: isString
  }),
  answers: isArrayOf(typeGuard({
    answer: isEither([isQuestionAnswer, typeGuard({
      id: isString
    })]),
    points: isNumber,
    // correct: isBoolean,
    responded: isBoolean
  })),

  /** custom field - not in this spot in the GraphQL Schema (data from `OtherResponse`)) */
  answerOther: isOptional(typeGuard({
    textAnswer: isOptional(isString)
  })),
  totalPoints: isNumber
};
const isQuestionHeaderImage = typeGuard({
  imageOptionId: isString,
  s3Key: isMaybe(isString),
  url: isMaybe(isString)
});
const isQuestionHeaderRandomAssignment = typeGuard({
  percent: isNumber,
  variableId: isString,
  variableName: isMaybe(isString),
  position: isNumber
});
const isSurveyQuestionHeader = typeGuard({
  randomAssignment: isMaybe(isQuestionHeaderRandomAssignment),
  image: isMaybe(isQuestionHeaderImage),
  heading: isMaybe(isString),
  description: isString
});
const isSurveyQuestion = typeGuard({ ...baseGuards$a,
  pageId: isString,
  validation: typeGuard({
    isRequired: isBoolean
  }),
  position: isNumber,
  absolutePosition: isNumber,
  displayNumber: isNumber,
  type: typeGuard({// not done yet
  }),
  visible: isBoolean,
  displayOptions: typeGuard({
    showDisplayNum: isBoolean
  }),
  headers: isArrayOf(isSurveyQuestionHeader),
  nickname: isString,
  isAudience: isBoolean
});
const isQuestionLayoutPlacement = isOneOf(questionTypeLayoutPlacements);
const isQuestionLayoutSpacing = typeGuard({
  left: isNumber,
  right: isNumber,
  top: isNumber,
  bottom: isNumber
});
const isQuestionLayoutWidth = isEither([typeGuard({
  format: isFixed('PERCENT'),
  width: isOneOf(relativeWidthOptions)
}), typeGuard({
  format: isFixed('FIXED'),
  width: isOneOf(fixedWidthOptions)
})]);
const isAnswerOtherLayout = typeGuard({
  linesCount: isNumber,
  charsCount: isNumber
});
/** Internal non-nullable version */

const isAnswerOtherCommentNonNullable = typeGuard({
  id: isString,
  label: isString,
  visible: isBoolean,
  type: isFixed('COMMENT'),
  layout: isAnswerOtherLayout
});
/** Internal non-nullable version */

const isAnswerOtherChoiceNonNullable = typeGuard({
  id: isString,
  label: isString,
  visible: isBoolean,
  type: isFixed('ANSWER'),
  requiredErrorText: isString,
  layout: isAnswerOtherLayout
});
const isAnswerOther = isNullable(isEither([isAnswerOtherChoiceNonNullable, isAnswerOtherCommentNonNullable]));
const isAnswerOtherComment = isNullable(isAnswerOtherCommentNonNullable);
const isAnswerNoneOfTheAbove = isNullable(typeGuard({
  id: isString,
  label: isString,
  visible: isBoolean
}));
const isFieldSet = typeGuard({
  id: isString,
  label: isOptional(isString),
  visible: isBoolean
});
/** Internal non-nullable version */

const isWeightedAnswer = typeGuard({
  id: isString,
  label: isString,
  weight: isNumber,
  visible: isBoolean
});
const isWeightedAnswers = isArrayOf(isWeightedAnswer);
const isQuestionSorting = isNullable(typeGuard({
  ignoreLast: isBoolean,
  type: isOneOf(questionTypeSortingKinds)
}));
const isQuestionSkipLogicRules = isArrayOf(typeGuard({
  id: isString,
  skipType: isOneOf(questionTypeSkipLogicTypes),
  sourceAnswer: isEither([isQuestionAnswer, isAnswerOtherChoiceNonNullable, isWeightedAnswer]),
  destination: isEither([typeGuard({
    type: isOneOf(['DISQUALIFY_RESPONDENT', 'END_SURVEY'])
  }), // ensure `SKIP_TO_PAGE` also has a question prop
  typeGuard({
    type: isFixed('SKIP_TO_PAGE'),
    question: isSurveyQuestion
  })])
}));
const isExternalQuestionWithTypeId = typeGuard({
  typeId: isString
});
const isAllAnswerRequired = typeGuard({
  type: isFixed('ALL'),
  errorMessage: isString
});
const isAnswerRangeRequired = typeGuard({
  type: isFixed('RANGE'),
  errorMessage: isString,
  min: isNumber,
  max: isNumber
});
const isQuestionAnswerRequiredAnswerCount = typeGuard({
  type: isOneOf(['AT_LEAST', 'AT_MOST', 'EXACTLY']),
  errorMessage: isString,
  min: isNumber,
  max: isNumber // This likely changes in the future

});

function assertExternalQuestionWithIds(u) {
  return typeAssertion({
    typeId: isString,
    id: isString
  })(u);
}
/** Only used for Respondent Survey */


const basicExternalAllMultipleChoiceGuards = {
  required: isNullable(isAllAnswerRequired)
};
const basicExternalMultipleChoiceAnswersGuard = {
  answers: isArrayOf(isQuestionAnswer)
};
const basicExternalMultipleChoiceValidationGuard = {
  validation: isMaybe(isFieldValidation)
};
const basicExternalMultipleChoiceLayoutGuard = {
  layout: typeGuard({
    answerLayout: isOneOf(questionTypeMultipleChoiceLayouts),
    placement: isQuestionLayoutPlacement,
    width: isQuestionLayoutWidth,
    spacing: isQuestionLayoutSpacing
  })
};
const basicExternalMultipleChoiceAnswerOtherGuard = {
  answerOther: isAnswerOther
};
/** for `answerNA` field */

const basicExternalMultipleChoiceAnswerNoneOfTheAboveGuard = {
  answerNA: isMaybe(isQuestionAnswer)
};
/** Shared ExternalQuestion Guards for Respondent-Survey capability */

const externalRespondentSurveyGuards = { ...positionGuard,
  heading: headingGuards,
  hasPiping: isBoolean
};
const isAggregateResponseHeaderItem$1 = typeGuard({
  id: isString,
  label: isString,
  weight: isNullable(isNumber)
});
const isAggregateResponseHeader$1 = typeGuard({
  id: isString,
  items: isArrayOf(isAggregateResponseHeaderItem$1)
});
/** Shared ExternalQuestion Guards for Respondent-Quiz-Results capability */

const externalRespondentQuizResultsGuards = { ...positionGuard,
  heading: headingGuards,
  quizResults: isNullable(typeGuard(externalQuizResultsGuards))
};
/** Shared ExternalQuestion Guards for AggregateResponse-Dashboard capability */

const externalAggregateResponseDashboardGuards = {
  dashboardType: isOptional(isEither([isFixed('slide'), isFixed('list'), isFixed('sentiment-chart')])),
  filterCount: isOptional(isNumber),
  selectedCount: isOptional(isNumber),
  skippedCount: isOptional(isNumber),
  answeredCount: isOptional(isNumber),
  responseData: isOptional(isNullable(isArrayOf(typeGuard({
    content: isString,
    id: isString,
    sentiment: isEither([isFixed('positive'), isFixed('negative'), isFixed('neutral'), isFixed('unknown')])
  })))),
  aggregateResponse: isOptional(typeGuard({
    columnHeader: isNullable(isAggregateResponseHeader$1),
    rowHeader: isAggregateResponseHeader$1,
    groupHeader: isNullable(isAggregateResponseHeader$1),
    data: isArrayOf(typeGuard({
      columnItem: isNullable(isAggregateResponseHeaderItem$1),
      rowItem: isNullable(isAggregateResponseHeaderItem$1),
      groupItem: isNullable(isAggregateResponseHeaderItem$1),
      percentage: isNumber,
      total: isNumber
    })),
    id: isString,
    stats: isArrayOf(typeGuard({
      across: isNullable(isEither([isFixed('COLUMNS'), isFixed('GROUPS'), isFixed('ROWS')])),
      row: isNullable(isAggregateResponseHeaderItem$1),
      column: isNullable(isAggregateResponseHeaderItem$1),
      group: isNullable(isAggregateResponseHeaderItem$1)
    })),
    type: isEither([isFixed('ONE_D'), isFixed('TWO_D'), isFixed('THREE_D')])
  })),
  heading: headingGuards
};
/** List of all smQuestion supported capabilities (others might get defined via custom providers/views) */

const CAPABILITIES = ['respondent-survey', 'respondent-quiz-results', 'aggregate-response-dashboard'];

function assertExternalQuestionByCapability(externalQuestion, guards) {
  return typeAssertion(guards)(externalQuestion);
}

function respondentSurveyMapping(externalQuestion) {
  assertExternalQuestionByCapability(externalQuestion, externalRespondentSurveyGuards);
  const {
    heading,
    position
  } = externalQuestion;
  return {
    questionNumber: position.label,
    title: heading.default.text,
    disabled: false
  };
}

function respondentQuizResultsMapping(externalQuestion) {
  assertExternalQuestionByCapability(externalQuestion, externalRespondentQuizResultsGuards);
  return {
    quizResults: externalQuestion.quizResults || undefined,
    questionNumber: externalQuestion.position.label,
    title: externalQuestion.heading.default.text
  };
}

function aggregateResponseDashboardMapping(externalQuestion) {
  assertExternalQuestionByCapability(externalQuestion, externalAggregateResponseDashboardGuards);
  const {
    dashboardType,
    filterCount,
    selectedCount,
    skippedCount,
    answeredCount,
    responseData,
    aggregateResponse,
    heading: {
      default: {
        text: title
      }
    }
  } = externalQuestion;
  return {
    dashboardType,
    filterCount,
    answeredCount,
    selectedCount,
    skippedCount,
    responseData,
    aggregateResponse,
    title
  };
}
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */


function assertBasicFields(externalQuestion) {
  return typeAssertion(baseGuards$a)(externalQuestion);
}

function basicFieldsMapping(externalQuestion) {
  assertBasicFields(externalQuestion);
  const {
    id
  } = externalQuestion;
  return {
    id
  };
}
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */


const basicExternalImageChoiceGuards = {
  family: isFixed('MULTIPLE_CHOICE'),
  variant: isEither([isFixed('MANY_ANSWERS_IMAGE'), isFixed('SINGLE_ANSWER_IMAGE')])
};

function assertExternalImageChoice(externalQuestion) {
  return typeAssertion(basicExternalImageChoiceGuards)(externalQuestion);
}
/** Map fields shared by all capabilities */


const mapExternalToInternalImageChoice = externalQuestion => {
  assertExternalImageChoice(externalQuestion);
  const {
    variant,
    family
  } = externalQuestion;
  return {
    typeId: 'image_choice',
    variant,
    family,
    disabled: false
  };
};

const answersImageGuards = {
  answersImage: isArrayOf(typeGuard({
    id: isString,
    image: typeGuard({
      altText: isString,
      url: isString
    }),
    label: isString,
    visible: isBoolean
  }))
};
/** Assertion for Respondent-Survey */

function assertRespondentSurvey$a(u) {
  return typeAssertion({ ...basicExternalAllMultipleChoiceGuards,
    ...basicExternalMultipleChoiceAnswerNoneOfTheAboveGuard,
    ...answersImageGuards,
    skipLogicRules: isQuestionSkipLogicRules
  })(u);
}

const mapRespondentSurveyExternalImageChoice = externalQuestion => {
  assertRespondentSurvey$a(externalQuestion);
  const {
    answerNA,
    answersImage,
    required,
    skipLogicRules,
    sorting
  } = externalQuestion;
  return {
    answerNoneOfTheAbove: answerNA,
    answersImage,
    required,
    skipLogicRules,
    sorting
  };
};
/** Assertion for Respondent-Quiz-Results */


function assertRespondentQuizResults$2(u) {
  return typeAssertion(typeGuard({ ...basicExternalMultipleChoiceAnswerNoneOfTheAboveGuard,
    ...answersImageGuards
  }))(u);
}
/** Map Respondent-Quiz-Results specific properties */


const mapRespondentQuizResultsExternalImageChoice = externalQuestion => {
  assertRespondentQuizResults$2(externalQuestion);
  const {
    answerNA,
    answersImage
  } = externalQuestion;
  return {
    answerNoneOfTheAbove: answerNA,
    answersImage
  };
}; // Respondent-Survey


const imageChoiceRespondentSurveyMapping = externalQuestion => ({ ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalImageChoice(externalQuestion),
  ...mapRespondentSurveyExternalImageChoice(externalQuestion)
}); // Respondent-Quiz-Results


const imageChoiceRespondentQuizResultsMapping = externalQuestion => ({ ...basicFieldsMapping(externalQuestion),
  ...respondentQuizResultsMapping(externalQuestion),
  ...mapExternalToInternalImageChoice(externalQuestion),
  ...mapRespondentQuizResultsExternalImageChoice(externalQuestion)
}); // Aggregate-Response-Dashboard


const imageChoiceAggregateResponseDashboardMapping = externalQuestion => ({ ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalImageChoice(externalQuestion) // ...mapAggregateResponseDashboardExternalImageChoice(externalQuestion), //does not exist yet

});

const mapRespondentQuizResultsAnswerOther = answerOther => {
  if (!answerOther) {
    return null;
  }

  let base;

  if (answerOther.type === 'ANSWER') {
    const addRequiredErrorText = answerOther.requiredErrorText !== null && answerOther.requiredErrorText !== undefined;
    base = {
      type: 'ANSWER',
      ...(addRequiredErrorText ? {
        requiredErrorText: answerOther.requiredErrorText
      } : {})
    };
  } else {
    base = {
      type: answerOther.type
    };
  }

  return { ...base,
    id: answerOther.id,
    label: answerOther.label,
    visible: answerOther.visible,
    linesCount: answerOther.layout.linesCount,
    charsCount: answerOther.layout.charsCount
  };
};

const mapRespondentSurveyAnswerOther = answerOther => {
  if (!answerOther) {
    return null;
  }

  let base;

  if (answerOther.type === 'ANSWER') {
    const addRequiredErrorText = answerOther.requiredErrorText !== null && answerOther.requiredErrorText !== undefined;
    base = {
      type: 'ANSWER',
      ...(addRequiredErrorText ? {
        requiredErrorText: answerOther.requiredErrorText
      } : {})
    };
  } else {
    base = {
      type: answerOther.type
    };
  }

  return { ...base,
    id: answerOther.id,
    label: answerOther.label,
    visible: answerOther.visible,
    touched: false,
    value: '',
    selected: false,
    linesCount: answerOther.layout.linesCount,
    charsCount: answerOther.layout.charsCount
  };
};

const fillSpacing = spacing => {
  var _a, _b, _c, _d;

  return spacing ? {
    top: (_a = spacing.top) !== null && _a !== void 0 ? _a : 0,
    bottom: (_b = spacing.bottom) !== null && _b !== void 0 ? _b : 0,
    left: (_c = spacing.left) !== null && _c !== void 0 ? _c : 0,
    right: (_d = spacing.right) !== null && _d !== void 0 ? _d : 0
  } : undefined;
};
/** Guard to check properties shared by all capabilities */


const basicExternalCheckBoxGuards = {
  family: isFixed('MULTIPLE_CHOICE'),
  variant: isFixed('MANY_ANSWERS_CHECKBOX')
};
/** Assertion to check properties shared by all capabilities */

function assertExternalCheckBox(externalQuestion) {
  return typeAssertion(basicExternalCheckBoxGuards)(externalQuestion);
}
/** Map fields shared by all capabilities */


const mapExternalToInternalCheckBox = externalQuestion => {
  assertExternalCheckBox(externalQuestion);
  const {
    variant,
    family
  } = externalQuestion;
  return {
    variant,
    family,
    disabled: false
  };
};
/** Assertion for Respondent-Survey specific properties */


function assertRespondentSurvey$9(u) {
  return typeAssertion(typeGuard({ ...basicExternalMultipleChoiceAnswersGuard,
    ...basicExternalMultipleChoiceAnswerOtherGuard,
    ...basicExternalMultipleChoiceAnswerNoneOfTheAboveGuard,
    ...basicExternalMultipleChoiceLayoutGuard,
    ...basicExternalMultipleChoiceValidationGuard,
    ...isNullable(isAllAnswerRequired)
  }))(u);
}
/** Map Respondent-Survey specific properties */


const mapRespondentSurveyExternalCheckBox = externalQuestion => {
  assertRespondentSurvey$9(externalQuestion);
  const {
    layout,
    validation,
    required,
    answerOther,
    answerNA
  } = externalQuestion;
  return {
    layout,
    required,
    answerOther: mapRespondentSurveyAnswerOther(answerOther),
    answerNoneOfTheAbove: answerNA,
    ...(validation ? {
      validation
    } : {})
  };
};
/** Assertion for Respondent-Quiz-Results */


function assertRespondentQuizResults$1(u) {
  return typeAssertion(typeGuard({ ...basicExternalMultipleChoiceLayoutGuard,
    ...basicExternalMultipleChoiceAnswerOtherGuard,
    ...basicExternalMultipleChoiceAnswerNoneOfTheAboveGuard
  }))(u);
}

const mapRespondentQuizResultsExternalCheckBox = externalQuestion => {
  assertRespondentQuizResults$1(externalQuestion);
  const {
    layout,
    answerOther,
    answerNA
  } = externalQuestion;
  return {
    layout: { ...layout,
      spacing: fillSpacing(layout.spacing)
    },
    answerOther: mapRespondentQuizResultsAnswerOther(answerOther),
    answerNoneOfTheAbove: answerNA !== null && answerNA !== void 0 ? answerNA : null // remove `null` as possible type

  };
}; // Respondent-Survey


const checkBoxRespondentSurveyMapping = externalQuestion => ({
  typeId: 'check_box',
  ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalCheckBox(externalQuestion),
  ...mapRespondentSurveyExternalCheckBox(externalQuestion)
}); // Respondent-Quiz-Results


const checkBoxRespondentQuizResultsMapping = externalQuestion => ({
  typeId: 'check_box',
  ...basicFieldsMapping(externalQuestion),
  ...respondentQuizResultsMapping(externalQuestion),
  ...mapExternalToInternalCheckBox(externalQuestion),
  ...mapRespondentQuizResultsExternalCheckBox(externalQuestion)
}); // Aggregate-Response-Dashboard


const checkBoxAggregateResponseDashboardMapping = externalQuestion => ({
  typeId: 'check_box',
  ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalCheckBox(externalQuestion) // ...mapAggregateResponseDashboardExternalCheckBox(externalQuestion), // does not exist yet

});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */


const basicExternalDropdownGuards = {
  variant: isFixed('SINGLE_ANSWER_MENU'),
  family: isFixed('MULTIPLE_CHOICE')
};

function assertExternalDropdown(externalQuestion) {
  return typeAssertion(basicExternalDropdownGuards)(externalQuestion);
}
/** Map fields shared by all capabilities */


const mapExternalToInternalDropdown = externalQuestion => {
  assertExternalDropdown(externalQuestion);
  const {
    variant,
    family
  } = externalQuestion;
  return {
    variant,
    family,
    disabled: false
  };
};
/** Assertion for Respondent-Survey specific properties */


function assertRespondentSurvey$8(u) {
  return typeAssertion(typeGuard({ ...basicExternalAllMultipleChoiceGuards,
    ...basicExternalMultipleChoiceLayoutGuard,
    ...basicExternalMultipleChoiceValidationGuard,
    ...basicExternalMultipleChoiceAnswersGuard,
    ...basicExternalMultipleChoiceAnswerOtherGuard
  }))(u);
}
/** Map Respondent-Survey specific properties */


const mapRespondentSurveyExternalDropdown = externalQuestion => {
  assertRespondentSurvey$8(externalQuestion); // Placeholder - we don't have this visualization yet

  const {
    layout,
    validation,
    answerOther,
    required,
    answers
  } = externalQuestion;
  return {
    required,
    layout,
    answers,
    answerOther: mapRespondentSurveyAnswerOther(answerOther),
    ...(validation ? {
      validation
    } : {})
  };
};
/** Assertion for Respondent-Survey specific properties */


function assertQuizResults(u) {
  return typeAssertion(typeGuard({ ...basicExternalMultipleChoiceAnswerOtherGuard,
    ...basicExternalMultipleChoiceLayoutGuard
  }))(u);
}

const mapRespondentQuizResultsExternalDropdown = externalQuestion => {
  assertQuizResults(externalQuestion);
  const {
    layout,
    answerOther
  } = externalQuestion;
  return {
    layout: { ...layout,
      spacing: fillSpacing(layout.spacing)
    },
    answerOther: mapRespondentQuizResultsAnswerOther(answerOther)
  };
}; // Respondent-Survey


const dropdownRespondentSurveyMapping = externalQuestion => ({
  typeId: 'dropdown',
  ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalDropdown(externalQuestion),
  ...mapRespondentSurveyExternalDropdown(externalQuestion)
}); // Respondent-Quiz-Results


const dropdownRespondentQuizResultsMapping = externalQuestion => ({
  typeId: 'dropdown',
  ...basicFieldsMapping(externalQuestion),
  ...respondentQuizResultsMapping(externalQuestion),
  ...mapExternalToInternalDropdown(externalQuestion),
  ...mapRespondentQuizResultsExternalDropdown(externalQuestion)
}); // Aggregate-Response-Dashboard


const dropdownAggregateResponseDashboardMapping = externalQuestion => ({
  typeId: 'dropdown',
  ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalDropdown(externalQuestion) // ...mapAggregateResponseDashboardExternalDropdown(externalQuestion), //does not exist yet

});

const basicExternalFieldSetGuards = {
  fieldSet: isFieldSet
};
const basicExternalRequiredGuards = {
  required: isNullable(isAllAnswerRequired)
};
const basicExternalOpenEndedLayoutGuards = {
  layout: typeGuard({
    placement: isQuestionLayoutPlacement,
    spacing: isQuestionLayoutSpacing,
    linesCount: isNumber,
    charsCount: isNumber
  })
};
const basicExternalOpenEndedValidationGuards = {
  validation: isMaybe(isFieldValidation)
};
const basicExternalCommentBoxGuards = {
  family: isFixed('OPEN_ENDED'),
  variant: isFixed('ESSAY')
};

function assertExternalCommentBox(externalQuestion) {
  return typeAssertion(basicExternalCommentBoxGuards)(externalQuestion);
}

const mapExternalToInternalCommentBox = externalQuestion => {
  assertExternalCommentBox(externalQuestion);
  const {
    variant,
    family
  } = externalQuestion;
  return {
    variant,
    family,
    disabled: false
  };
};
/** Assertion for Respondent-Survey */


function assertRespondentSurvey$7(u) {
  return typeAssertion({ ...basicExternalFieldSetGuards,
    ...basicExternalRequiredGuards,
    ...basicExternalOpenEndedLayoutGuards,
    ...basicExternalOpenEndedValidationGuards
  })(u);
}

const mapRespondentSurveyExternalCommentBox = externalQuestion => {
  assertRespondentSurvey$7(externalQuestion);
  const {
    layout,
    validation,
    required,
    fieldSet
  } = externalQuestion;
  return {
    layout: { ...layout,
      spacing: fillSpacing(layout.spacing)
    },
    required,
    fieldSet,
    ...(validation ? {
      validation
    } : {})
  };
}; // Respondent-Survey


const commentBoxRespondentSurveyMapping = externalQuestion => ({
  typeId: 'comment_box',
  ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalCommentBox(externalQuestion),
  ...mapRespondentSurveyExternalCommentBox(externalQuestion)
}); // Aggregate-Response-Dashboard


const commentBoxAggregateResponseDashboardMapping = externalQuestion => ({
  typeId: 'comment_box',
  ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalCommentBox(externalQuestion) // ...mapAggregateResponseDashboardExternalCommentBox(externalQuestion), //does not exist yet

});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */


const basicExternalSingleTextboxGuards = {
  family: isFixed('OPEN_ENDED'),
  variant: isFixed('SINGLE_LINE')
};

function assertExternalSingleTextbox(externalQuestion) {
  return typeAssertion(basicExternalSingleTextboxGuards)(externalQuestion);
}
/** Map fields shared by all capabilities */


const mapExternalToInternalSingleTextbox = externalQuestion => {
  assertExternalSingleTextbox(externalQuestion);
  const {
    variant,
    family
  } = externalQuestion;
  return {
    variant,
    family,
    disabled: false
  };
};
/** Assertion for Respondent-Survey */


function assertRespondentSurvey$6(u) {
  return typeAssertion({ ...basicExternalFieldSetGuards,
    ...basicExternalRequiredGuards,
    ...basicExternalOpenEndedLayoutGuards,
    ...basicExternalOpenEndedValidationGuards
  })(u);
}

const mapRespondentSurveyExternalSingleTextbox = externalQuestion => {
  assertRespondentSurvey$6(externalQuestion);
  const {
    layout,
    validation,
    required,
    fieldSet
  } = externalQuestion;
  return {
    layout: { ...layout,
      spacing: fillSpacing(layout.spacing)
    },
    required,
    fieldSet,
    ...(validation ? {
      validation
    } : {})
  };
}; // Respondent-Survey


const singleTextboxRespondentSurveyMapping = externalQuestion => ({
  typeId: 'single_textbox',
  ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalSingleTextbox(externalQuestion),
  ...mapRespondentSurveyExternalSingleTextbox(externalQuestion)
}); // Aggregate-Response-Dashboard


const singleTextboxAggregateResponseDashboardMapping = externalQuestion => ({
  typeId: 'single_textbox',
  ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalSingleTextbox(externalQuestion) // ...mapAggregateResponseDashboardExternalSingleTextbox(externalQuestion), //does not exist yet

});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

/** Guard to check properties shared by all capabilities */


const basicExternalMatrixGuards = {
  family: isFixed('MATRIX'),
  variant: isEither([isFixed('SINGLE_ANSWER_RADIO'), isFixed('MANY_ANSWERS_CHECKBOX')])
};

function assertExternalMatrix(externalQuestion) {
  return typeAssertion(basicExternalMatrixGuards)(externalQuestion);
}
/** Map fields shared by all capabilities */


const mapExternalToInternalMatrix = externalQuestion => {
  assertExternalMatrix(externalQuestion);
  const {
    family,
    variant
  } = externalQuestion;
  return {
    family,
    variant,
    disabled: false
  };
};
/** Assertion for Respondent-Survey specific properties */


function assertRespondentSurvey$5(u) {
  return typeAssertion(typeGuard({
    fieldSets: isArrayOf(isFieldSet),
    required: isNullable(isEither([isAllAnswerRequired, isAnswerRangeRequired, isQuestionAnswerRequiredAnswerCount])),
    sorting: isNullable(isQuestionSorting),
    layout: typeGuard({
      placement: isQuestionLayoutPlacement,
      spacing: isQuestionLayoutSpacing,
      width: isQuestionLayoutWidth,
      labelWidthPercent: isNumber
    })
  }))(u);
}
/** Map Respondent-Survey specific properties */


const mapRespondentSurveyExternalMatrix = externalQuestion => {
  assertRespondentSurvey$5(externalQuestion);
  const {
    layout,
    fieldSets,
    required,
    sorting
  } = externalQuestion;
  return {
    layout: { ...layout,
      spacing: fillSpacing(layout.spacing)
    },
    fieldSets,
    required,
    sorting
  };
}; // Respondent-Survey


const matrixRespondentSurveyMapping = externalQuestion => ({
  typeId: 'matrix',
  ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalMatrix(externalQuestion),
  ...mapRespondentSurveyExternalMatrix(externalQuestion)
}); // Aggregate-Response-Dashboard


const matrixAggregateResponseDashboardMapping = externalQuestion => ({
  typeId: 'matrix',
  ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalMatrix(externalQuestion) // ...mapAggregateResponseDashboardExternalMatrix(externalQuestion), //does not exist yet

});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

/** Guard to check properties shared by all capabilities */


const basicExternalMultipleChoiceGuards = {
  family: isFixed('MULTIPLE_CHOICE'),
  variant: isFixed('SINGLE_ANSWER_RADIO')
};
/** Assertion to check properties shared by all capabilities */

function assertExternalMultipleChoice(externalQuestion) {
  return typeAssertion(basicExternalMultipleChoiceGuards)(externalQuestion);
}
/** Map fields shared by all capabilities */


const mapExternalToInternalMultipleChoice = externalQuestion => {
  assertExternalMultipleChoice(externalQuestion);
  const {
    variant,
    family
  } = externalQuestion;
  return {
    variant,
    family,
    disabled: false
  };
};
/** Assertion for Respondent-Survey specific properties */


function assertRespondentSurvey$4(u) {
  return typeAssertion(typeGuard({ ...basicExternalAllMultipleChoiceGuards,
    ...basicExternalMultipleChoiceLayoutGuard,
    ...basicExternalMultipleChoiceValidationGuard,
    ...basicExternalMultipleChoiceAnswersGuard,
    ...basicExternalMultipleChoiceAnswerOtherGuard,
    ...basicExternalMultipleChoiceAnswerNoneOfTheAboveGuard
  }))(u);
}
/** Map Respondent-Survey specific properties */


const mapRespondentSurveyExternalMultipleChoice = externalQuestion => {
  assertRespondentSurvey$4(externalQuestion); // Placeholder - we don't have this visualization yet

  const {
    required,
    layout,
    validation,
    answers,
    answerOther,
    answerNA
  } = externalQuestion;
  return {
    required,
    layout,
    answers,
    answerOther: mapRespondentSurveyAnswerOther(answerOther),
    answerNoneOfTheAbove: answerNA,
    ...(validation ? {
      validation
    } : {})
  };
};
/** Assertion for Respondent-Quiz-Results */


function assertRespondentQuizResults(u) {
  return typeAssertion(typeGuard({ ...basicExternalMultipleChoiceLayoutGuard,
    ...basicExternalMultipleChoiceAnswerOtherGuard,
    ...basicExternalMultipleChoiceAnswerNoneOfTheAboveGuard
  }))(u);
}
/** Map Respondent-Quiz-Results specific properties */


const mapRespondentQuizResultsExternalMultipleChoice = externalQuestion => {
  assertRespondentQuizResults(externalQuestion);
  const {
    layout,
    answerOther,
    answerNA
  } = externalQuestion;
  return {
    layout: { ...layout,
      spacing: fillSpacing(layout.spacing)
    },
    answerOther: mapRespondentQuizResultsAnswerOther(answerOther),
    answerNoneOfTheAbove: answerNA || null
  };
}; // Respondent-Survey


const multipleChoiceRespondentSurveyMapping = externalQuestion => ({
  typeId: 'multiple_choice',
  ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalMultipleChoice(externalQuestion),
  ...mapRespondentSurveyExternalMultipleChoice(externalQuestion)
}); // Respondent-Quiz-Results


const multipleChoiceRespondentQuizResultsMapping = externalQuestion => ({
  typeId: 'multiple_choice',
  ...basicFieldsMapping(externalQuestion),
  ...respondentQuizResultsMapping(externalQuestion),
  ...mapExternalToInternalMultipleChoice(externalQuestion),
  ...mapRespondentQuizResultsExternalMultipleChoice(externalQuestion)
}); // Aggregate-Response-Dashboard


const multipleChoiceAggregateResponseDashboardMapping = externalQuestion => ({
  typeId: 'multiple_choice',
  ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalMultipleChoice(externalQuestion) // ...mapAggregateResponseDashboardExternalMultipleChoice(externalQuestion), //does not exist yet

});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */


const basicExternalMultipleTextboxGuards = {
  family: isFixed('OPEN_ENDED'),
  variant: isFixed('MANY_FIELDS')
};

function assertExternalMultipleTextbox(externalQuestion) {
  return typeAssertion(basicExternalMultipleTextboxGuards)(externalQuestion);
}
/** Map external question type */


const mapExternalToInternalMultipleTextbox = externalQuestion => {
  assertExternalMultipleTextbox(externalQuestion);
  const {
    variant,
    family
  } = externalQuestion;
  return {
    variant,
    family,
    disabled: false
  };
};
/** Assertion for Respondent-Survey */


function assertRespondentSurvey$3(u) {
  return typeAssertion({
    fieldSets: isArrayOf(isFieldSet),
    required: isAllAnswerRequired,
    layout: typeGuard({
      placement: isQuestionLayoutPlacement,
      spacing: isQuestionLayoutSpacing,
      width: isQuestionLayoutWidth,
      labelWidthPercent: isNumber,
      charsCount: isNumber
    }),
    sorting: isNullable(isQuestionSorting),
    validation: isOptional(isNullable(isEither([isSumValidation, isFieldValidation])))
  })(u);
}

const mapRespondentSurveyExternalMultipleTextbox = externalQuestion => {
  assertRespondentSurvey$3(externalQuestion);
  const {
    fieldSets,
    layout,
    required,
    sorting,
    validation
  } = externalQuestion;
  return {
    fieldSets,
    layout,
    required,
    sorting,
    ...(validation ? {
      validation
    } : {})
  };
}; // Respondent-Survey


const multipleTextboxRespondentSurveyMapping = externalQuestion => ({
  typeId: 'multiple_textbox',
  ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalMultipleTextbox(externalQuestion),
  ...mapRespondentSurveyExternalMultipleTextbox(externalQuestion) // does not exist yet

}); // Aggregate-Response-Dashboard


const multipleTextboxAggregateResponseDashboardMapping = externalQuestion => ({
  typeId: 'multiple_textbox',
  ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalMultipleTextbox(externalQuestion) // ...mapAggregateResponseDashboardExternalMultipleTextbox(externalQuestion), //does not exist yet

});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */


const basicExternalRankingGuards = {
  family: isFixed('RANKING'),
  variant: isFixed('SINGLE_ANSWER_RADIO')
};

function assertExternalRanking(externalQuestion) {
  return typeAssertion(basicExternalRankingGuards)(externalQuestion);
}
/** Map external question type */


const mapExternalToInternalRanking = externalQuestion => {
  assertExternalRanking(externalQuestion);
  const {
    family,
    variant
  } = externalQuestion;
  return {
    variant,
    family,
    disabled: false
  };
};

const isRankingLayoutOption = typeGuard({
  spacing: isQuestionLayoutSpacing,
  width: isQuestionLayoutWidth,
  labelWidthPercent: isNumber
});
/** Assertion for Respondent-Survey */

function assertRespondentSurvey$2(u) {
  return typeAssertion({
    required: isNullable(isAllAnswerRequired),
    answerNA: isNullable(isAnswerNoneOfTheAbove),
    fieldSets: isArrayOf(isFieldSet),
    layout: isRankingLayoutOption,
    sorting: isNullable(isQuestionSorting)
  })(u);
}
/** Map the respondent survey fields */


const mapRespondentSurveyExternalRanking = externalQuestion => {
  assertRespondentSurvey$2(externalQuestion);
  const {
    required,
    answerNA,
    fieldSets,
    layout,
    sorting
  } = externalQuestion;
  return {
    required,
    answerNA,
    fieldSets,
    layout,
    sorting
  };
}; // Respondent-Survey


const rankingRespondentSurveyMapping = externalQuestion => ({
  typeId: 'ranking',
  ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalRanking(externalQuestion),
  ...mapRespondentSurveyExternalRanking(externalQuestion)
}); // Aggregate-Response-Dashboard


const rankingAggregateResponseDashboardMapping = externalQuestion => ({
  typeId: 'ranking',
  ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalRanking(externalQuestion) // ...mapAggregateResponseDashboardExternalRanking(externalQuestion), //does not exist yet

});

const isSliderScaleLabels = typeGuard({
  left: isString,
  middle: isOptional(isNullable(isString)),
  right: isString
});
const isScaleOptions = typeGuard({
  minValue: isNumber,
  maxValue: isNumber,
  errorMessage: isString,
  stepSize: isNumber,
  startPosition: isNumber,
  isCustom: isBoolean,
  labels: isSliderScaleLabels
});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

const basicExternalSliderGuards = {
  family: isFixed('SLIDER'),
  variant: isFixed('SINGLE_SLIDER')
};

function assertExternalSlider(externalQuestion) {
  return typeAssertion(basicExternalSliderGuards)(externalQuestion);
}
/** Map external question type */


const mapExternalToInternalSlider = externalQuestion => {
  assertExternalSlider(externalQuestion);
  const {
    family,
    variant
  } = externalQuestion;
  return {
    variant,
    family,
    disabled: false
  };
};
/** Assertion for Respondent-Survey */


function assertRespondentSurvey$1(u) {
  return typeAssertion({
    required: isNullable(isAnswerRangeRequired),
    scaleOptions: isScaleOptions,
    hideNumericalInput: isBoolean
  })(u);
}
/** Map the respondent survey fields */


const mapRespondentSurveyExternalSlider = externalQuestion => {
  assertRespondentSurvey$1(externalQuestion);
  const {
    required,
    scaleOptions,
    hideNumericalInput
  } = externalQuestion;
  return {
    required,
    scaleOptions,
    hideNumericalInput
  };
}; // Respondent-Survey


const sliderRespondentSurveyMapping = externalQuestion => ({
  typeId: 'slider',
  ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalSlider(externalQuestion),
  ...mapRespondentSurveyExternalSlider(externalQuestion)
}); // Aggregate-Response-Dashboard


const sliderAggregateResponseDashboardMapping = externalQuestion => ({
  typeId: 'slider',
  ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalSlider(externalQuestion) // ...mapAggregateResponseDashboardExternalSlider(externalQuestion), //does not exist yet

});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */


const basicExternalStarRatingGuards = {
  family: isFixed('RATING_SCALE'),
  variant: isFixed('SINGLE_ANSWER_SYMBOL')
};

function assertExternalStarRating(externalQuestion) {
  return typeAssertion(basicExternalStarRatingGuards)(externalQuestion);
}
/** Map external question type */


const mapExternalToInternalStarRating = externalQuestion => {
  assertExternalStarRating(externalQuestion);
  const {
    family,
    variant
  } = externalQuestion;
  return {
    variant,
    family,
    disabled: false
  };
};

const isRatingScaleSymbolOptions = typeGuard({
  color: isString,
  symbol: isOneOf(ratingScaleSymbols)
});
/** Assertion for Respondent-Survey */

function assertRespondentSurvey(u) {
  return typeAssertion({
    required: isNullable(isAllAnswerRequired),
    fieldSet: isOptional(isFieldSet),
    layout: typeGuard({
      placement: isQuestionLayoutPlacement,
      spacing: isQuestionLayoutSpacing,
      width: isQuestionLayoutWidth
    }),
    answersWeighted: isWeightedAnswers,
    answerNA: isAnswerNoneOfTheAbove,
    answerOther: isAnswerOtherComment,
    symbolOptions: isRatingScaleSymbolOptions
  })(u);
}
/** Map the respondent survey fields */


const mapRespondentSurveyExternalStarRating = externalQuestion => {
  assertRespondentSurvey(externalQuestion);
  const {
    required,
    fieldSet,
    layout,
    answersWeighted,
    answerNA,
    answerOther,
    symbolOptions
  } = externalQuestion;
  return {
    required,
    fieldSet,
    layout,
    answersWeighted,
    answerNA,
    answerOther,
    symbolOptions
  };
}; // Respondent-Survey


const starRatingRespondentSurveyMapping = externalQuestion => ({
  typeId: 'star_rating',
  ...basicFieldsMapping(externalQuestion),
  ...respondentSurveyMapping(externalQuestion),
  ...mapExternalToInternalStarRating(externalQuestion),
  ...mapRespondentSurveyExternalStarRating(externalQuestion)
}); // Aggregate-Response-Dashboard


const starRatingAggregateResponseDashboardMapping = externalQuestion => ({
  typeId: 'star_rating',
  ...basicFieldsMapping(externalQuestion),
  ...aggregateResponseDashboardMapping(externalQuestion),
  ...mapExternalToInternalStarRating(externalQuestion) // ...mapAggregateResponseDashboardExternalStarRating(externalQuestion), //does not exist yet

});
/* eslint-disable @typescript-eslint/naming-convention */


const invalidCombination = () => {
  throw new Error('Invalid question and capability combination');
};
/**
 * Main mapping to resolve QuestionType and Capability to
 * a mapping (and in it the assertion)
 */


const questionTypeAndCapabilityMapping = {
  single_textbox: {
    'respondent-survey': singleTextboxRespondentSurveyMapping,
    'respondent-quiz-results': invalidCombination,
    'aggregate-response-dashboard': singleTextboxAggregateResponseDashboardMapping
  },
  comment_box: {
    'respondent-survey': commentBoxRespondentSurveyMapping,
    'respondent-quiz-results': invalidCombination,
    'aggregate-response-dashboard': commentBoxAggregateResponseDashboardMapping
  },
  multiple_textbox: {
    'respondent-survey': multipleTextboxRespondentSurveyMapping,
    'respondent-quiz-results': invalidCombination,
    'aggregate-response-dashboard': multipleTextboxAggregateResponseDashboardMapping
  },
  check_box: {
    'respondent-survey': checkBoxRespondentSurveyMapping,
    'respondent-quiz-results': checkBoxRespondentQuizResultsMapping,
    'aggregate-response-dashboard': checkBoxAggregateResponseDashboardMapping
  },
  image_choice: {
    'respondent-survey': imageChoiceRespondentSurveyMapping,
    'respondent-quiz-results': imageChoiceRespondentQuizResultsMapping,
    'aggregate-response-dashboard': imageChoiceAggregateResponseDashboardMapping
  },
  multiple_choice: {
    'respondent-survey': multipleChoiceRespondentSurveyMapping,
    'respondent-quiz-results': multipleChoiceRespondentQuizResultsMapping,
    'aggregate-response-dashboard': multipleChoiceAggregateResponseDashboardMapping
  },
  dropdown: {
    'respondent-survey': dropdownRespondentSurveyMapping,
    'respondent-quiz-results': dropdownRespondentQuizResultsMapping,
    'aggregate-response-dashboard': dropdownAggregateResponseDashboardMapping
  },
  slider: {
    'respondent-survey': sliderRespondentSurveyMapping,
    'respondent-quiz-results': invalidCombination,
    'aggregate-response-dashboard': sliderAggregateResponseDashboardMapping
  },
  star_rating: {
    'respondent-survey': starRatingRespondentSurveyMapping,
    'respondent-quiz-results': invalidCombination,
    'aggregate-response-dashboard': starRatingAggregateResponseDashboardMapping
  },
  ranking: {
    'respondent-survey': rankingRespondentSurveyMapping,
    'respondent-quiz-results': invalidCombination,
    'aggregate-response-dashboard': rankingAggregateResponseDashboardMapping
  },
  matrix: {
    'respondent-survey': matrixRespondentSurveyMapping,
    'respondent-quiz-results': invalidCombination,
    'aggregate-response-dashboard': matrixAggregateResponseDashboardMapping
  }
};
/** type guard to check if string is a capability */

const isCapability = u => CAPABILITIES.includes(u);
/** Used to dynamically map the fields base on
 * * typeId
 * * question type
 * * capability
 * * combination of question type and capability
 */


const mapDynamic = ({
  typeId,
  capability,
  externalQuestion
}) => {
  if (!isCapability(capability)) {
    // if this is a custom (unknown) capability just return the `externalQuestion` for now
    return externalQuestion;
  }

  return questionTypeAndCapabilityMapping[typeId][capability](externalQuestion);
};

const mapInternalDynamicToExternal = (externalQuestion, // eslint-disable-next-line @typescript-eslint/no-unused-vars
q, // eslint-disable-next-line @typescript-eslint/no-unused-vars
capability // keep for future use
) => {
  // possibly mirror `mapDynamic`s setup if needed later
  // remove internal `typeId` and other possible attributes from exported type
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    typeId,
    ...cleanExternalQuestion
  } = externalQuestion;
  return { ...cleanExternalQuestion
  };
};
/**
 *
 * Detects if a question belongs to this question type -
 * minimalistically as this function is supposed to be as optimized as possible as it will run multiple times
 */


const isCompatible$b = (q, typeId) => 'typeId' in q && q.typeId === typeId;

const baseGuards$9 = baseQuestionGuards('single_textbox', 'OPEN_ENDED', 'SINGLE_LINE');
const isQuestionTypeSingleTextbox = typeGuard(baseGuards$9);

function assertQuestionTypeSingleTextbox(q) {
  return typeAssertion(baseGuards$9)(q);
}

const mapFromExternalQuestion$a = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'single_textbox',
    externalQuestion,
    capability
  });
  assertQuestionTypeSingleTextbox(questionType);
  return questionType;
};

const typeId$a = 'single_textbox';

const fromExternalQuestion$a = (source, capability) => mapFromExternalQuestion$a(source, capability);

const toExternalQuestion$a = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const isCompatible$a = q => isCompatible$b(q, typeId$a);

const isOfQuestionType$a = isQuestionTypeSingleTextbox;
const assertQuestionType$a = assertQuestionTypeSingleTextbox;
/** Helper to run validators and prepare their result */

const runValidators = (
/** Map of validators */
validators, question, value, touched) => {
  const validationErrors = validators.reduce((errors, validator) => {
    const newError = validator(question, value, touched);
    return newError !== null ? [...errors, newError] : errors;
  }, []);

  if (validationErrors.length === 0) {
    return null;
  }

  return validationErrors;
};

function createValidationError(type, detail = '', questionId = null) {
  return {
    type,
    detail,
    questionId
  };
}
/** returns `null` if valid and error object if not */


const validateTextLength = (
/** GraphQL `TextLengthValidation` entity  */
validation,
/** Id to identify the question */
questionId, value = '', touched) => {
  if (!value || !validation || !touched || validation.type !== 'TEXT_LENGTH') {
    return null;
  }

  const {
    minLength,
    maxLength,
    errorMessage
  } = validation;

  if (value.length > maxLength || value.length < minLength) {
    return createValidationError(validation.type, errorMessage, questionId);
  }

  return null;
};
/** Validates `valueToValidate` for text-length and returns an `QuestionError` or `null` (valid) */


const checkValueTextLength = (question, valueToValidate, touched) => {
  var _a;

  if (question.validation === undefined || !valueToValidate || ((_a = question.validation) === null || _a === void 0 ? void 0 : _a.type) !== 'TEXT_LENGTH') {
    return null; // questionType does not support validation
  }

  return validateTextLength(question.validation, question.id, valueToValidate, touched);
};

const DATE_PATTERNS = {
  DATE_INTL: /(((0|1)[0-9]|2[0-9]|3[0-1])\/(0[1-9]|1[0-2])\/(\d{4}))$/,
  DATE_US: /(0[1-9]|1[0-2])\/(((0|1)[0-9]|2[0-9]|3[0-1])\/(\d{4}))$/
};
const DATE_BUILDERS = {
  DATE_INTL: ([dd, mm, yyyy]) => new Date(yyyy, mm - 1, dd),
  DATE_US: ([mm, dd, yyyy]) => new Date(yyyy, mm - 1, dd)
};

const isDateValidationType = validationType => validationType === 'DATE_INTL' || validationType === 'DATE_US';
/** formats strings to dates
 *
 * - `DD/MM/YYYY` for `DATE_INTL`
 * - `MM/DD/YYYY` for `DATE_US`
 */


const stringToDate = format => s => DATE_PATTERNS[format].test(s) ? DATE_BUILDERS[format](s.split('/').map(Number)) : null;
/** returns `null` if valid and error object if not */


const validateDate = (
/** GraphQL `DateValidation` entity  */
validation,
/** Id to identify the question */
questionId, value = '', touched) => {
  if (!value || !validation || !touched || !isDateValidationType(validation.type)) {
    return null;
  }

  const {
    minDate,
    maxDate,
    errorMessage
  } = validation;
  const dateType = validation.type;
  const stringToDateType = stringToDate(dateType); // value format: Short Date => Date

  const date = stringToDateType(value); // minDate and maxDate format: ISO Date-Time => Date

  const [dmin, dmax] = [minDate, maxDate].map(s => new Date(s.substring(0, s.indexOf('T')).replace('-', '/')));

  if (!date || dmax && date.getTime() > dmax.getTime() || dmin && date.getTime() < dmin.getTime()) {
    return createValidationError(validation.type, errorMessage, questionId);
  }

  return null;
};
/** Validates `valueToValidate` as date and returns an `QuestionError` or `null` (valid) */


const checkValueDateFormat = (question, valueToValidate, touched) => {
  var _a;

  if (!valueToValidate || !isDateValidationType((_a = question === null || question === void 0 ? void 0 : question.validation) === null || _a === void 0 ? void 0 : _a.type)) {
    return null; // questionType does not support validation
  }

  return validateDate(question.validation, question.id, valueToValidate, touched);
};

const isNumericalValidationType = validationType => validationType === 'INTEGER' || validationType === 'DECIMAL';
/** Integers can only contain numbers (`0-9`) */


const isValidInteger = (maybeInteger = '') => maybeInteger.match(/^[0-9]+$/) !== null;
/** returns `null` if valid and error object if not */


const validateNumber = (
/** GraphQL `DateValidation` entity  */
validation,
/** Id to identify the question */
questionId, value = '', touched) => {
  if (!value || !validation || !touched || !isNumericalValidationType(validation.type)) {
    return null;
  }

  const {
    minValue,
    maxValue,
    errorMessage
  } = validation;
  const parsedNumber = parseFloat(value);

  if (Number.isNaN(parsedNumber) || validation.type === 'INTEGER' && !isValidInteger(value) || maxValue && parsedNumber > maxValue || minValue && parsedNumber < minValue) {
    return createValidationError(validation.type, errorMessage, questionId);
  }

  return null;
};
/** Validates `valueToValidate` as number and returns an `QuestionError` or `null` (valid) */


const checkValueNumericalFormat = (question, valueToValidate, touched) => {
  var _a;

  if (!valueToValidate || !isNumericalValidationType((_a = question === null || question === void 0 ? void 0 : question.validation) === null || _a === void 0 ? void 0 : _a.type)) {
    return null; // questionType does not support validation
  }

  return validateNumber(question.validation, question.id, valueToValidate, touched);
};

function getDefaultExportFromCjs(x) {
  return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var isEmail$1 = {
  exports: {}
};
var assertString = {
  exports: {}
};

(function (module, exports) {
  Object.defineProperty(exports, "__esModule", {
    value: true
  });
  exports.default = assertString;

  function _typeof(obj) {
    "@babel/helpers - typeof";

    if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
      _typeof = function _typeof(obj) {
        return typeof obj;
      };
    } else {
      _typeof = function _typeof(obj) {
        return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
      };
    }

    return _typeof(obj);
  }

  function assertString(input) {
    var isString = typeof input === 'string' || input instanceof String;

    if (!isString) {
      var invalidType = _typeof(input);

      if (input === null) invalidType = 'null';else if (invalidType === 'object') invalidType = input.constructor.name;
      throw new TypeError("Expected a string but received a ".concat(invalidType));
    }
  }

  module.exports = exports.default;
  module.exports.default = exports.default;
})(assertString, assertString.exports);

var merge = {
  exports: {}
};

(function (module, exports) {
  Object.defineProperty(exports, "__esModule", {
    value: true
  });
  exports.default = merge;

  function merge() {
    var obj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var defaults = arguments.length > 1 ? arguments[1] : undefined;

    for (var key in defaults) {
      if (typeof obj[key] === 'undefined') {
        obj[key] = defaults[key];
      }
    }

    return obj;
  }

  module.exports = exports.default;
  module.exports.default = exports.default;
})(merge, merge.exports);

var isByteLength = {
  exports: {}
};

(function (module, exports) {
  Object.defineProperty(exports, "__esModule", {
    value: true
  });
  exports.default = isByteLength;

  var _assertString = _interopRequireDefault(assertString.exports);

  function _interopRequireDefault(obj) {
    return obj && obj.__esModule ? obj : {
      default: obj
    };
  }

  function _typeof(obj) {
    "@babel/helpers - typeof";

    if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
      _typeof = function _typeof(obj) {
        return typeof obj;
      };
    } else {
      _typeof = function _typeof(obj) {
        return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
      };
    }

    return _typeof(obj);
  }
  /* eslint-disable prefer-rest-params */


  function isByteLength(str, options) {
    (0, _assertString.default)(str);
    var min;
    var max;

    if (_typeof(options) === 'object') {
      min = options.min || 0;
      max = options.max;
    } else {
      // backwards compatibility: isByteLength(str, min [, max])
      min = arguments[1];
      max = arguments[2];
    }

    var len = encodeURI(str).split(/%..|./).length - 1;
    return len >= min && (typeof max === 'undefined' || len <= max);
  }

  module.exports = exports.default;
  module.exports.default = exports.default;
})(isByteLength, isByteLength.exports);

var isFQDN = {
  exports: {}
};

(function (module, exports) {
  Object.defineProperty(exports, "__esModule", {
    value: true
  });
  exports.default = isFQDN;

  var _assertString = _interopRequireDefault(assertString.exports);

  var _merge = _interopRequireDefault(merge.exports);

  function _interopRequireDefault(obj) {
    return obj && obj.__esModule ? obj : {
      default: obj
    };
  }

  var default_fqdn_options = {
    require_tld: true,
    allow_underscores: false,
    allow_trailing_dot: false,
    allow_numeric_tld: false
  };

  function isFQDN(str, options) {
    (0, _assertString.default)(str);
    options = (0, _merge.default)(options, default_fqdn_options);
    /* Remove the optional trailing dot before checking validity */

    if (options.allow_trailing_dot && str[str.length - 1] === '.') {
      str = str.substring(0, str.length - 1);
    }

    var parts = str.split('.');
    var tld = parts[parts.length - 1];

    if (options.require_tld) {
      // disallow fqdns without tld
      if (parts.length < 2) {
        return false;
      }

      if (!/^([a-z\u00a1-\uffff]{2,}|xn[a-z0-9-]{2,})$/i.test(tld)) {
        return false;
      } // disallow spaces && special characers


      if (/[\s\u2002-\u200B\u202F\u205F\u3000\uFEFF\uDB40\uDC20\u00A9\uFFFD]/.test(tld)) {
        return false;
      }
    } // reject numeric TLDs


    if (!options.allow_numeric_tld && /^\d+$/.test(tld)) {
      return false;
    }

    return parts.every(function (part) {
      if (part.length > 63) {
        return false;
      }

      if (!/^[a-z_\u00a1-\uffff0-9-]+$/i.test(part)) {
        return false;
      } // disallow full-width chars


      if (/[\uff01-\uff5e]/.test(part)) {
        return false;
      } // disallow parts starting or ending with hyphen


      if (/^-|-$/.test(part)) {
        return false;
      }

      if (!options.allow_underscores && /_/.test(part)) {
        return false;
      }

      return true;
    });
  }

  module.exports = exports.default;
  module.exports.default = exports.default;
})(isFQDN, isFQDN.exports);

var isIP = {
  exports: {}
};

(function (module, exports) {
  Object.defineProperty(exports, "__esModule", {
    value: true
  });
  exports.default = isIP;

  var _assertString = _interopRequireDefault(assertString.exports);

  function _interopRequireDefault(obj) {
    return obj && obj.__esModule ? obj : {
      default: obj
    };
  }
  /**
  11.3.  Examples
  
     The following addresses
  
               fe80::1234 (on the 1st link of the node)
               ff02::5678 (on the 5th link of the node)
               ff08::9abc (on the 10th organization of the node)
  
     would be represented as follows:
  
               fe80::1234%1
               ff02::5678%5
               ff08::9abc%10
  
     (Here we assume a natural translation from a zone index to the
     <zone_id> part, where the Nth zone of any scope is translated into
     "N".)
  
     If we use interface names as <zone_id>, those addresses could also be
     represented as follows:
  
              fe80::1234%ne0
              ff02::5678%pvc1.3
              ff08::9abc%interface10
  
     where the interface "ne0" belongs to the 1st link, "pvc1.3" belongs
     to the 5th link, and "interface10" belongs to the 10th organization.
   * * */


  var ipv4Maybe = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
  var ipv6Block = /^[0-9A-F]{1,4}$/i;

  function isIP(str) {
    var version = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    (0, _assertString.default)(str);
    version = String(version);

    if (!version) {
      return isIP(str, 4) || isIP(str, 6);
    } else if (version === '4') {
      if (!ipv4Maybe.test(str)) {
        return false;
      }

      var parts = str.split('.').sort(function (a, b) {
        return a - b;
      });
      return parts[3] <= 255;
    } else if (version === '6') {
      var addressAndZone = [str]; // ipv6 addresses could have scoped architecture
      // according to https://tools.ietf.org/html/rfc4007#section-11

      if (str.includes('%')) {
        addressAndZone = str.split('%');

        if (addressAndZone.length !== 2) {
          // it must be just two parts
          return false;
        }

        if (!addressAndZone[0].includes(':')) {
          // the first part must be the address
          return false;
        }

        if (addressAndZone[1] === '') {
          // the second part must not be empty
          return false;
        }
      }

      var blocks = addressAndZone[0].split(':');
      var foundOmissionBlock = false; // marker to indicate ::
      // At least some OS accept the last 32 bits of an IPv6 address
      // (i.e. 2 of the blocks) in IPv4 notation, and RFC 3493 says
      // that '::ffff:a.b.c.d' is valid for IPv4-mapped IPv6 addresses,
      // and '::a.b.c.d' is deprecated, but also valid.

      var foundIPv4TransitionBlock = isIP(blocks[blocks.length - 1], 4);
      var expectedNumberOfBlocks = foundIPv4TransitionBlock ? 7 : 8;

      if (blocks.length > expectedNumberOfBlocks) {
        return false;
      } // initial or final ::


      if (str === '::') {
        return true;
      } else if (str.substr(0, 2) === '::') {
        blocks.shift();
        blocks.shift();
        foundOmissionBlock = true;
      } else if (str.substr(str.length - 2) === '::') {
        blocks.pop();
        blocks.pop();
        foundOmissionBlock = true;
      }

      for (var i = 0; i < blocks.length; ++i) {
        // test for a :: which can not be at the string start/end
        // since those cases have been handled above
        if (blocks[i] === '' && i > 0 && i < blocks.length - 1) {
          if (foundOmissionBlock) {
            return false; // multiple :: in address
          }

          foundOmissionBlock = true;
        } else if (foundIPv4TransitionBlock && i === blocks.length - 1) ;else if (!ipv6Block.test(blocks[i])) {
          return false;
        }
      }

      if (foundOmissionBlock) {
        return blocks.length >= 1;
      }

      return blocks.length === expectedNumberOfBlocks;
    }

    return false;
  }

  module.exports = exports.default;
  module.exports.default = exports.default;
})(isIP, isIP.exports);

(function (module, exports) {
  Object.defineProperty(exports, "__esModule", {
    value: true
  });
  exports.default = isEmail;

  var _assertString = _interopRequireDefault(assertString.exports);

  var _merge = _interopRequireDefault(merge.exports);

  var _isByteLength = _interopRequireDefault(isByteLength.exports);

  var _isFQDN = _interopRequireDefault(isFQDN.exports);

  var _isIP = _interopRequireDefault(isIP.exports);

  function _interopRequireDefault(obj) {
    return obj && obj.__esModule ? obj : {
      default: obj
    };
  }

  function _slicedToArray(arr, i) {
    return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
  }

  function _nonIterableRest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  function _unsupportedIterableToArray(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(o);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
  }

  function _arrayLikeToArray(arr, len) {
    if (len == null || len > arr.length) len = arr.length;

    for (var i = 0, arr2 = new Array(len); i < len; i++) {
      arr2[i] = arr[i];
    }

    return arr2;
  }

  function _iterableToArrayLimit(arr, i) {
    if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;

    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);

        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"] != null) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }

    return _arr;
  }

  function _arrayWithHoles(arr) {
    if (Array.isArray(arr)) return arr;
  }

  var default_email_options = {
    allow_display_name: false,
    require_display_name: false,
    allow_utf8_local_part: true,
    require_tld: true,
    blacklisted_chars: '',
    ignore_max_length: false
  };
  /* eslint-disable max-len */

  /* eslint-disable no-control-regex */

  var splitNameAddress = /^([^\x00-\x1F\x7F-\x9F\cX]+)<(.+)>$/i;
  var emailUserPart = /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~]+$/i;
  var gmailUserPart = /^[a-z\d]+$/;
  var quotedEmailUser = /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f]))*$/i;
  var emailUserUtf8Part = /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+$/i;
  var quotedEmailUserUtf8 = /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*$/i;
  var defaultMaxEmailLength = 254;
  /* eslint-enable max-len */

  /* eslint-enable no-control-regex */

  /**
   * Validate display name according to the RFC2822: https://tools.ietf.org/html/rfc2822#appendix-A.1.2
   * @param {String} display_name
   */

  function validateDisplayName(display_name) {
    var trim_quotes = display_name.match(/^"(.+)"$/i);
    var display_name_without_quotes = trim_quotes ? trim_quotes[1] : display_name; // display name with only spaces is not valid

    if (!display_name_without_quotes.trim()) {
      return false;
    } // check whether display name contains illegal character


    var contains_illegal = /[\.";<>]/.test(display_name_without_quotes);

    if (contains_illegal) {
      // if contains illegal characters,
      // must to be enclosed in double-quotes, otherwise it's not a valid display name
      if (!trim_quotes) {
        return false;
      } // the quotes in display name must start with character symbol \


      var all_start_with_back_slash = display_name_without_quotes.split('"').length === display_name_without_quotes.split('\\"').length;

      if (!all_start_with_back_slash) {
        return false;
      }
    }

    return true;
  }

  function isEmail(str, options) {
    (0, _assertString.default)(str);
    options = (0, _merge.default)(options, default_email_options);

    if (options.require_display_name || options.allow_display_name) {
      var display_email = str.match(splitNameAddress);

      if (display_email) {
        var display_name;

        var _display_email = _slicedToArray(display_email, 3);

        display_name = _display_email[1];
        str = _display_email[2]; // sometimes need to trim the last space to get the display name
        // because there may be a space between display name and email address
        // eg. myname <address@gmail.com>
        // the display name is `myname` instead of `myname `, so need to trim the last space

        if (display_name.endsWith(' ')) {
          display_name = display_name.substr(0, display_name.length - 1);
        }

        if (!validateDisplayName(display_name)) {
          return false;
        }
      } else if (options.require_display_name) {
        return false;
      }
    }

    if (!options.ignore_max_length && str.length > defaultMaxEmailLength) {
      return false;
    }

    var parts = str.split('@');
    var domain = parts.pop();
    var user = parts.join('@');
    var lower_domain = domain.toLowerCase();

    if (options.domain_specific_validation && (lower_domain === 'gmail.com' || lower_domain === 'googlemail.com')) {
      /*
        Previously we removed dots for gmail addresses before validating.
        This was removed because it allows `multiple..dots@gmail.com`
        to be reported as valid, but it is not.
        Gmail only normalizes single dots, removing them from here is pointless,
        should be done in normalizeEmail
      */
      user = user.toLowerCase(); // Removing sub-address from username before gmail validation

      var username = user.split('+')[0]; // Dots are not included in gmail length restriction

      if (!(0, _isByteLength.default)(username.replace('.', ''), {
        min: 6,
        max: 30
      })) {
        return false;
      }

      var _user_parts = username.split('.');

      for (var i = 0; i < _user_parts.length; i++) {
        if (!gmailUserPart.test(_user_parts[i])) {
          return false;
        }
      }
    }

    if (options.ignore_max_length === false && (!(0, _isByteLength.default)(user, {
      max: 64
    }) || !(0, _isByteLength.default)(domain, {
      max: 254
    }))) {
      return false;
    }

    if (!(0, _isFQDN.default)(domain, {
      require_tld: options.require_tld
    })) {
      if (!options.allow_ip_domain) {
        return false;
      }

      if (!(0, _isIP.default)(domain)) {
        if (!domain.startsWith('[') || !domain.endsWith(']')) {
          return false;
        }

        var noBracketdomain = domain.substr(1, domain.length - 2);

        if (noBracketdomain.length === 0 || !(0, _isIP.default)(noBracketdomain)) {
          return false;
        }
      }
    }

    if (user[0] === '"') {
      user = user.slice(1, user.length - 1);
      return options.allow_utf8_local_part ? quotedEmailUserUtf8.test(user) : quotedEmailUser.test(user);
    }

    var pattern = options.allow_utf8_local_part ? emailUserUtf8Part : emailUserPart;
    var user_parts = user.split('.');

    for (var _i2 = 0; _i2 < user_parts.length; _i2++) {
      if (!pattern.test(user_parts[_i2])) {
        return false;
      }
    }

    if (options.blacklisted_chars) {
      if (user.search(new RegExp("[".concat(options.blacklisted_chars, "]+"), 'g')) !== -1) return false;
    }

    return true;
  }

  module.exports = exports.default;
  module.exports.default = exports.default;
})(isEmail$1, isEmail$1.exports);

var isEmail = /*@__PURE__*/getDefaultExportFromCjs(isEmail$1.exports);
/** returns `null` if valid and error object if not */

const validateEmail = (validation,
/** Id to identify the question */
questionId, value = '', touched) => {
  if (!value || !validation || !touched || (validation === null || validation === void 0 ? void 0 : validation.type) !== 'EMAIL') {
    return null;
  }

  const {
    errorMessage
  } = validation;

  if (!isEmail(value)) {
    return createValidationError(validation.type, errorMessage, questionId);
  }

  return null;
};
/** Validates `valueToValidate` as email and returns an `QuestionError` or `null` (valid) */


const checkValueEmailFormat = (question, valueToValidate, touched) => {
  var _a;

  if (question.validation === undefined || !valueToValidate || ((_a = question.validation) === null || _a === void 0 ? void 0 : _a.type) !== 'EMAIL') {
    return null; // questionType does not support validation
  }

  return validateEmail(question.validation, question.id, valueToValidate, touched);
};

const isInvalidArray = value => {
  if (value.length === 0) {
    return true;
  }

  return false;
};
/** returns `null` if valid and error object if not */


const validateRequired = (required,
/** Id to identify the question */
questionId, value, touched) => {
  if (!touched || !required || required.type !== 'ALL') {
    return null;
  }

  let hasError = false; // add more value type specific checks here as we add more questionTypes

  switch (typeof value) {
    case 'undefined':
      hasError = true;
      break;

    case 'string':
      if (value.length <= 0) {
        hasError = true;
      }

      break;

    case 'number':
      if (Number.isNaN(value)) {
        hasError = true;
      }

      break;

    case 'object':
      if (value === null) {
        hasError = true;
        break;
      }

      if (Array.isArray(value)) {
        if (isInvalidArray(value)) {
          hasError = true;
        }

        break;
      }

      break;
  }

  if (hasError) {
    return createValidationError('REQUIRED', required.errorMessage, questionId);
  }

  return null;
};
/** Validates `valueToValidate` for text-length and returns an `QuestionError` or `null` (valid) */


const checkValueRequired = (question, valueToValidate, touched) => {
  if (!(question === null || question === void 0 ? void 0 : question.required)) {
    return null; // questionType does not support validation or question is not touched
  }

  return validateRequired(question.required, question.id, valueToValidate, touched);
};
/** Helper for question-types that only validate require */


const standAloneRequiredValidator = (question, valueToValidate, touched) => {
  const valueError = checkValueRequired(question, valueToValidate, touched);
  return valueError === null ? null : [valueError];
};
/** Array of all validators needed to validate the content of openEnded
 * text inputs (SingleTextbox, `other`, `answerOther`) */


const openEndedValidators = [checkValueTextLength, checkValueNumericalFormat, checkValueDateFormat, checkValueEmailFormat];

const validateRespondentSurvey$1 = (q, value, touched) => {
  return runValidators([checkValueRequired, ...openEndedValidators], q, value, touched);
};

const singleTextboxQuestionDefinition = {
  typeId: typeId$a,
  fromExternalQuestion: fromExternalQuestion$a,
  toExternalQuestion: toExternalQuestion$a,
  isOfQuestionType: isOfQuestionType$a,
  isCompatible: isCompatible$a,
  assertQuestionType: assertQuestionType$a
};
const baseGuards$8 = baseQuestionGuards('multiple_textbox', 'OPEN_ENDED', 'MANY_FIELDS');
const isQuestionTypeMultipleTextbox = typeGuard(baseGuards$8);

function assertQuestionTypeMultipleTextbox(q) {
  return typeAssertion(baseGuards$8)(q);
}

const mapFromExternalQuestion$9 = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'multiple_textbox',
    externalQuestion,
    capability
  });
  assertQuestionTypeMultipleTextbox(questionType);
  return questionType;
};

const typeId$9 = 'multiple_textbox';

const fromExternalQuestion$9 = (source, capability) => mapFromExternalQuestion$9(source, capability);

const toExternalQuestion$9 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const isCompatible$9 = q => isCompatible$b(q, typeId$9);

const isOfQuestionType$9 = isQuestionTypeMultipleTextbox;
const assertQuestionType$9 = assertQuestionTypeMultipleTextbox;
const multipleTextboxQuestionDefinition = {
  typeId: typeId$9,
  fromExternalQuestion: fromExternalQuestion$9,
  toExternalQuestion: toExternalQuestion$9,
  isOfQuestionType: isOfQuestionType$9,
  isCompatible: isCompatible$9,
  assertQuestionType: assertQuestionType$9
}; /// Type Guards

const baseGuards$7 = baseQuestionGuards('comment_box', 'OPEN_ENDED', 'ESSAY');
const isQuestionTypeCommentBox = typeGuard(baseGuards$7);

function assertQuestionTypeCommentBox(q) {
  return typeAssertion(baseGuards$7)(q);
}

const mapFromExternalQuestion$8 = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'comment_box',
    externalQuestion,
    capability
  });
  assertQuestionTypeCommentBox(questionType);
  return questionType;
};

const typeId$8 = 'comment_box';

const fromExternalQuestion$8 = (source, capability) => mapFromExternalQuestion$8(source, capability);

const toExternalQuestion$8 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const isCompatible$8 = q => isCompatible$b(q, typeId$8);

const isOfQuestionType$8 = isQuestionTypeCommentBox;
const assertQuestionType$8 = assertQuestionTypeCommentBox;
const validateRespondentSurvey = standAloneRequiredValidator;
const commentBoxQuestionDefinition = {
  typeId: typeId$8,
  fromExternalQuestion: fromExternalQuestion$8,
  toExternalQuestion: toExternalQuestion$8,
  isCompatible: isCompatible$8,
  isOfQuestionType: isOfQuestionType$8,
  assertQuestionType: assertQuestionType$8
}; /// Type Guards

const baseGuards$6 = baseQuestionGuards('multiple_choice', 'MULTIPLE_CHOICE', 'SINGLE_ANSWER_RADIO');
const isQuestionValidationMultipleChoice = isFieldValidation;
const isQuestionTypeMultipleChoice = typeGuard(baseGuards$6);

function assertQuestionTypeMultipleChoice(q) {
  return typeAssertion(baseGuards$6)(q);
}

const mapFromExternalQuestion$7 = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'multiple_choice',
    externalQuestion,
    capability
  });
  assertQuestionTypeMultipleChoice(questionType);
  return questionType;
};

const typeId$7 = 'multiple_choice';

const fromExternalQuestion$7 = (source, capability) => mapFromExternalQuestion$7(source, capability);

const toExternalQuestion$7 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const isCompatible$7 = q => isCompatible$b(q, typeId$7);

const isOfQuestionType$7 = isQuestionTypeMultipleChoice;
const assertQuestionType$7 = assertQuestionTypeMultipleChoice;
const multipleChoiceQuestionDefinition = {
  typeId: typeId$7,
  fromExternalQuestion: fromExternalQuestion$7,
  toExternalQuestion: toExternalQuestion$7,
  isCompatible: isCompatible$7,
  isOfQuestionType: isOfQuestionType$7,
  assertQuestionType: assertQuestionType$7
}; // TypeGuards and Assertions

const baseGuards$5 = baseQuestionGuards('check_box', 'MULTIPLE_CHOICE', 'MANY_ANSWERS_CHECKBOX');
const isQuestionTypeCheckBox = typeGuard(baseGuards$5);

function assertQuestionTypeCheckBox(externalQuestion) {
  return typeAssertion(baseGuards$5)(externalQuestion);
}

const mapFromExternalQuestion$6 = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'check_box',
    externalQuestion,
    capability
  });
  assertQuestionTypeCheckBox(questionType);
  return questionType;
};

const typeId$6 = 'check_box';

const fromExternalQuestion$6 = (source, capability) => mapFromExternalQuestion$6(source, capability);

const toExternalQuestion$6 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const isCompatible$6 = q => isCompatible$b(q, typeId$6);

const isOfQuestionType$6 = isQuestionTypeCheckBox;
const assertQuestionType$6 = assertQuestionTypeCheckBox;
const checkBoxQuestionDefinition = {
  typeId: typeId$6,
  fromExternalQuestion: fromExternalQuestion$6,
  toExternalQuestion: toExternalQuestion$6,
  isCompatible: isCompatible$6,
  isOfQuestionType: isOfQuestionType$6,
  assertQuestionType: assertQuestionType$6
};
const baseGuards$4 = baseQuestionGuards('dropdown', 'MULTIPLE_CHOICE', 'SINGLE_ANSWER_MENU');
const isQuestionTypeDropdown = typeGuard(baseGuards$4);

function assertQuestionTypeDropdown(externalQuestion) {
  return typeAssertion(baseGuards$4)(externalQuestion);
}

const mapFromExternalQuestion$5 = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'dropdown',
    externalQuestion,
    capability
  });
  assertQuestionTypeDropdown(questionType);
  return questionType;
};

const typeId$5 = 'dropdown';

const fromExternalQuestion$5 = (source, capability) => mapFromExternalQuestion$5(source, capability);

const toExternalQuestion$5 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const isCompatible$5 = q => isCompatible$b(q, typeId$5);

const isOfQuestionType$5 = isQuestionTypeDropdown;
const assertQuestionType$5 = assertQuestionTypeDropdown;
const dropdownQuestionDefinition = {
  typeId: typeId$5,
  fromExternalQuestion: fromExternalQuestion$5,
  toExternalQuestion: toExternalQuestion$5,
  isCompatible: isCompatible$5,
  isOfQuestionType: isOfQuestionType$5,
  assertQuestionType: assertQuestionType$5
};
const baseGuards$3 = baseQuestionGuards('image_choice', 'MULTIPLE_CHOICE', 'SINGLE_ANSWER_IMAGE');
const isQuestionTypeImageChoice = typeGuard({ ...baseGuards$3,

  /* To reduce code duplication, we assume the discrepancies between
   * "single answer" and "many answers" question types are limited to "typeId" and "variant".
   */
  typeId: isFixed('image_choice'),
  variant: isEither([isFixed('SINGLE_ANSWER_IMAGE'), isFixed('MANY_ANSWERS_IMAGE')])
});

function assertQuestionTypeImageChoice(q) {
  return typeAssertion({ ...baseGuards$3,

    /* To reduce code duplication, we assume the discrepancies between
     * "single answer" and "many answers" question types are limited to "typeId" and "variant".
     */
    typeId: isFixed('image_choice'),
    variant: isEither([isFixed('SINGLE_ANSWER_IMAGE'), isFixed('MANY_ANSWERS_IMAGE')])
  })(q);
}

const mapFromExternalQuestion$4 = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'image_choice',
    externalQuestion,
    capability
  });
  assertQuestionTypeImageChoice(questionType);
  return questionType;
};

const typeId$4 = 'image_choice';

const fromExternalQuestion$4 = (source, capability) => mapFromExternalQuestion$4(source, capability);

const toExternalQuestion$4 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const isCompatible$4 = q => isCompatible$b(q, typeId$4);

const isOfQuestionType$4 = isQuestionTypeImageChoice;
const assertQuestionType$4 = assertQuestionTypeImageChoice;
const imageChoiceQuestionDefinition = {
  typeId: typeId$4,
  fromExternalQuestion: fromExternalQuestion$4,
  toExternalQuestion: toExternalQuestion$4,
  isCompatible: isCompatible$4,
  isOfQuestionType: isOfQuestionType$4,
  assertQuestionType: assertQuestionType$4
};
const baseGuards$2 = baseQuestionGuards('slider', 'SLIDER', 'SINGLE_SLIDER');
const isQuestionTypeSlider = typeGuard(baseGuards$2); // export const assertQuestionTypeSlider =

function assertQuestionTypeSlider(externalQuestion) {
  return typeAssertion(baseGuards$2)(externalQuestion);
}

const mapFromExternalQuestion$3 = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'slider',
    externalQuestion,
    capability
  });
  assertQuestionTypeSlider(questionType);
  return questionType;
};

const typeId$3 = 'slider';

const isCompatible$3 = q => isCompatible$b(q, typeId$3);

const isOfQuestionType$3 = isQuestionTypeSlider;
const assertQuestionType$3 = assertQuestionTypeSlider;

const fromExternalQuestion$3 = (source, capability) => mapFromExternalQuestion$3(source, capability);

const toExternalQuestion$3 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const sliderQuestionDefinition = {
  typeId: typeId$3,
  fromExternalQuestion: fromExternalQuestion$3,
  toExternalQuestion: toExternalQuestion$3,
  isCompatible: isCompatible$3,
  isOfQuestionType: isOfQuestionType$3,
  assertQuestionType: assertQuestionType$3
};
const baseGuards$1 = baseQuestionGuards('star_rating', 'RATING_SCALE', 'SINGLE_ANSWER_SYMBOL');
const isQuestionTypeStarRating = typeGuard(baseGuards$1);

function assertQuestionTypeStarRating(externalQuestion) {
  return typeAssertion(baseGuards$1)(externalQuestion);
}

const mapFromExternalQuestion$2 = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'star_rating',
    externalQuestion,
    capability
  });
  assertQuestionTypeStarRating(questionType);
  return questionType;
};

const typeId$2 = 'star_rating';

const isCompatible$2 = q => isCompatible$b(q, typeId$2);

const isOfQuestionType$2 = isQuestionTypeStarRating;
const assertQuestionType$2 = assertQuestionTypeStarRating;

const fromExternalQuestion$2 = (source, capability) => mapFromExternalQuestion$2(source, capability);

const toExternalQuestion$2 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const starRatingQuestionDefinition = {
  typeId: typeId$2,
  fromExternalQuestion: fromExternalQuestion$2,
  toExternalQuestion: toExternalQuestion$2,
  isCompatible: isCompatible$2,
  isOfQuestionType: isOfQuestionType$2,
  assertQuestionType: assertQuestionType$2
};
const baseSingleAnswerGuards = baseQuestionGuards('matrix', 'MATRIX', 'SINGLE_ANSWER_RADIO');
const baseManyAnswersGuards = baseQuestionGuards('matrix', 'MATRIX', 'MANY_ANSWERS_CHECKBOX');
const isQuestionTypeMatrixSingleAnswer = typeGuard(baseSingleAnswerGuards);
const isQuestionTypeMatrixManyAnswers = typeGuard(baseManyAnswersGuards);
const isQuestionTypeMatrix = isEither([isQuestionTypeMatrixSingleAnswer, isQuestionTypeMatrixManyAnswers]);

function assertQuestionTypeMatrix(externalQuestion) {
  // TODO: Refactor for better error messages
  if (!isQuestionTypeMatrix(externalQuestion)) {
    throw new Error('Question data does not match any supported question type.');
  }
}

const mapFromExternalQuestion$1 = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'matrix',
    externalQuestion,
    capability
  });
  assertQuestionTypeMatrix(questionType);
  return questionType;
};

const typeId$1 = 'matrix';

const isCompatible$1 = q => isCompatible$b(q, typeId$1);

const isOfQuestionType$1 = isQuestionTypeMatrix;
const assertQuestionType$1 = assertQuestionTypeMatrix;

const fromExternalQuestion$1 = (source, capability) => mapFromExternalQuestion$1(source, capability);

const toExternalQuestion$1 = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const matrixQuestionDefinition = {
  typeId: typeId$1,
  fromExternalQuestion: fromExternalQuestion$1,
  toExternalQuestion: toExternalQuestion$1,
  isOfQuestionType: isOfQuestionType$1,
  isCompatible: isCompatible$1,
  assertQuestionType: assertQuestionType$1
};
const baseGuards = baseQuestionGuards('ranking', 'RANKING', 'SINGLE_ANSWER_RADIO'); // TODO: Remove Partial here

const isQuestionTypeRanking = typeGuard(baseGuards); // TODO: Remove Partial here

function assertQuestionTypeRanking(externalQuestion) {
  return typeAssertion(baseGuards)(externalQuestion);
}

const mapFromExternalQuestion = (externalQuestion, capability) => {
  const questionType = mapDynamic({
    typeId: 'ranking',
    externalQuestion,
    capability
  });
  assertQuestionTypeRanking(questionType);
  return questionType;
};

const typeId = 'ranking';

const isCompatible = q => isCompatible$b(q, typeId);

const isOfQuestionType = isQuestionTypeRanking;
const assertQuestionType = assertQuestionTypeRanking;

const fromExternalQuestion = (source, capability) => mapFromExternalQuestion(source, capability);

const toExternalQuestion = (eq, q, capability) => mapInternalDynamicToExternal(eq);

const rankingQuestionDefinition = {
  typeId,
  fromExternalQuestion,
  toExternalQuestion,
  isCompatible,
  isOfQuestionType,
  assertQuestionType
};
const questionTypeDefinitionModules = [singleTextboxQuestionDefinition, multipleTextboxQuestionDefinition, commentBoxQuestionDefinition, multipleChoiceQuestionDefinition, checkBoxQuestionDefinition, imageChoiceQuestionDefinition, dropdownQuestionDefinition, sliderQuestionDefinition, starRatingQuestionDefinition, matrixQuestionDefinition, rankingQuestionDefinition];

const findByType = typeId => qd => qd.typeId === typeId;

const pickQuestionTypeDefinition = typeId => {
  const res = questionTypeDefinitionModules.find(findByType(typeId));

  if (res === undefined) {
    throw new Error(`Unable to find module definition for type ${typeId}`);
  }

  return res;
};

const selectQuestionTypeDefinition = q => {
  var _a;

  return (_a = questionTypeDefinitionModules.find(({
    isCompatible
  }) => isCompatible(q))) !== null && _a !== void 0 ? _a : null;
};
/** Converts the external `family` and `variant` to the internally used `TypeId` */


const getTypeId = ({
  family,
  variant
}) => {
  switch (family) {
    case 'OPEN_ENDED':
      switch (variant) {
        case 'SINGLE_LINE':
          return 'single_textbox';

        case 'ESSAY':
          return 'comment_box';

        case 'MANY_FIELDS':
          return 'multiple_textbox';

        default:
          return null;
      }

    case 'MULTIPLE_CHOICE':
      switch (variant) {
        case 'SINGLE_ANSWER_RADIO':
          return 'multiple_choice';

        case 'MANY_ANSWERS_CHECKBOX':
          return 'check_box';

        case 'SINGLE_ANSWER_IMAGE':
        case 'MANY_ANSWERS_IMAGE':
          return 'image_choice';

        case 'SINGLE_ANSWER_MENU':
          return 'dropdown';

        default:
          return null;
      }

    case 'SLIDER':
      switch (variant) {
        case 'SINGLE_SLIDER':
          return 'slider';

        default:
          return null;
      }

    case 'RATING_SCALE':
      switch (variant) {
        case 'SINGLE_ANSWER_SYMBOL':
          return 'star_rating';

        default:
          return null;
      }

    case 'MATRIX':
      switch (variant) {
        case 'SINGLE_ANSWER_RADIO':
        case 'MANY_ANSWERS_CHECKBOX':
          return 'matrix';

        default:
          return null;
      }

    case 'RANKING':
      switch (variant) {
        case 'SINGLE_ANSWER_RADIO':
          return 'ranking';

        default:
          return null;
      }

    default:
      return null;
  }
}; /// Aggregate Response Guard Helpers
/// GQL Types that will be generated soon.


const isAggregateResponseHeaderItem = typeGuard({
  id: isString,
  label: isString,
  weight: isNullable(isNumber)
});
const isAggregateResponseHeader = typeGuard({
  id: isString,
  items: isArrayOf(isAggregateResponseHeaderItem)
});
const isAggregateResponseCell = typeGuard({
  rowItem: isNullable(isAggregateResponseHeaderItem),
  columnItem: isNullable(isAggregateResponseHeaderItem),
  groupItem: isNullable(isAggregateResponseHeaderItem),
  percentage: isNumber,
  total: isNumber
});
const aggregateResponseBasicStatDimension = ['ROWS', 'COLUMNS', 'GROUPS'];
const isAggregateResponseBasicStats = typeGuard({
  across: isNullable(isOneOf(aggregateResponseBasicStatDimension)),
  row: isNullable(isAggregateResponseHeaderItem),
  column: isNullable(isAggregateResponseHeaderItem),
  group: isNullable(isAggregateResponseHeaderItem)
}); // One dimensional aggregate response

const isOneDimensionalAggregateResponseCell = typeGuard({
  rowItem: isAggregateResponseHeaderItem,
  columnItem: isNull,
  groupItem: isNull,
  percentage: isNumber,
  total: isNumber
});
const isOneDimensionalAggregateResponse = typeGuard({
  id: isString,
  type: isFixed('ONE_D'),
  rowHeader: isAggregateResponseHeader,
  columnHeader: isNull,
  groupHeader: isNull,
  data: isArrayOf(isOneDimensionalAggregateResponseCell),
  stats: isArrayOf(isAggregateResponseBasicStats)
}); // Two dimensional aggregate response

const isTwoDimensionalAggregateResponseCell = typeGuard({
  rowItem: isAggregateResponseHeaderItem,
  columnItem: isAggregateResponseHeaderItem,
  groupItem: isNull,
  percentage: isNumber,
  total: isNumber
});
const isTwoDimensionalAggregateResponse = typeGuard({
  id: isString,
  type: isFixed('TWO_D'),
  rowHeader: isAggregateResponseHeader,
  columnHeader: isAggregateResponseHeader,
  groupHeader: isNull,
  data: isArrayOf(isTwoDimensionalAggregateResponseCell),
  stats: isArrayOf(isAggregateResponseBasicStats)
}); // Three dimensional aggregate response

const isThreeDimensionalAggregateResponseCell = typeGuard({
  rowItem: isAggregateResponseHeaderItem,
  columnItem: isAggregateResponseHeaderItem,
  groupItem: isAggregateResponseHeaderItem,
  percentage: isNumber,
  total: isNumber
});
const isThreeDimensionalAggregateResponse = typeGuard({
  id: isString,
  type: isFixed('THREE_D'),
  rowHeader: isAggregateResponseHeader,
  columnHeader: isAggregateResponseHeader,
  groupHeader: isAggregateResponseHeader,
  data: isArrayOf(isThreeDimensionalAggregateResponseCell),
  stats: isArrayOf(isAggregateResponseBasicStats)
}); // Transformed type guards

const isOneDimensionalChartData = typeGuard({
  id: isString,
  rowID: isString,
  rowLabel: isString,
  total: isNumber,
  percentage: isNumber
});
const isTwoDimensionalData = typeGuard({
  columnID: isString,
  columnLabel: isString,
  data: isArrayOf(isOneDimensionalChartData)
});
const isThreeDimensionalData = typeGuard({
  groupID: isString,
  groupLabel: isString,
  data: isArrayOf(isTwoDimensionalData)
});
export { SENTIMENTS, aggregateResponseBasicStatDimension, assertExternalQuestionWithIds, assertQuestionTypeCheckBox, assertQuestionTypeCommentBox, assertQuestionTypeDropdown, assertQuestionTypeImageChoice, assertQuestionTypeMatrix, assertQuestionTypeMultipleChoice, assertQuestionTypeMultipleTextbox, assertQuestionTypeRanking, assertQuestionTypeSingleTextbox, assertQuestionTypeSlider, assertQuestionTypeStarRating, baseQuestionGuards, checkBoxQuestionDefinition, commentBoxQuestionDefinition, validateRespondentSurvey as commentBoxRespondentSurveyValidation, dropdownQuestionDefinition, fixedWidthOptions, getTypeId, imageChoiceQuestionDefinition, isAggregateResponseCell, isAggregateResponseHeader, isAggregateResponseHeaderItem, isExternalQuestionWithTypeId, isOneDimensionalAggregateResponse, isOneDimensionalChartData, isQuestionError, isQuestionType, isQuestionTypeCheckBox, isQuestionTypeCommentBox, isQuestionTypeDropdown, isQuestionTypeImageChoice, isQuestionTypeMatrix, isQuestionTypeMultipleChoice, isQuestionTypeMultipleTextbox, isQuestionTypeRanking, isQuestionTypeSingleTextbox, isQuestionTypeSlider, isQuestionTypeStarRating, isQuestionValidationMultipleChoice, isThreeDimensionalAggregateResponse, isThreeDimensionalData, isTwoDimensionalAggregateResponse, isTwoDimensionalData, matrixQuestionDefinition, multipleChoiceQuestionDefinition, multipleTextboxQuestionDefinition, pickQuestionTypeDefinition, questionTypeFamilies, questionTypeIds, questionTypeLayoutPlacements, questionTypeMultipleChoiceLayouts, questionTypeRequiredPredicateTypes, questionTypeSkipLogicDestinationTypes, questionTypeSkipLogicTypes, questionTypeSortingKinds, questionTypeValidationTypes, questionTypeVariants, rankingQuestionDefinition, ratingScaleSymbols, relativeWidthOptions, selectQuestionTypeDefinition, singleTextboxQuestionDefinition, validateRespondentSurvey$1 as singleTextboxRespondentSurveyValidation, sliderQuestionDefinition, starRatingQuestionDefinition };